/**
 * ToastManager Component
 * 
 * Portal-based container that manages and renders all active toasts.
 * Positioned 24px to the right of the sidebar and 24px from the bottom.
 * Toasts stack from bottom to top (newest at bottom).
 * 
 * This component should be rendered once at the app root level.
 * 
 * @example
 * // In App.js
 * import ToastManager from './components/toast/ToastManager';
 * 
 * const App = () => (
 *   <>
 *     <YourAppContent />
 *     <ToastManager />
 *   </>
 * );
 */

import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { toastsSelector } from './toastSelectors';
import { dismissToast } from './toastActions';
import { sidebarCollapsedSelector } from '../sidebar/sidebar.selectors';
import { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED } from '../sidebar/sidebar.config';
import LytToast from './LytToast';
import { TOAST_POSITION, TOAST_PORTAL_ROOT_ID } from './toastConstants';

// ============================================================================
// Styled Components
// ============================================================================

const ToastContainer = styled.div`
    position: fixed;
    bottom: ${TOAST_POSITION.BOTTOM}px;
    left: ${({ $sidebarWidth }) => $sidebarWidth + TOAST_POSITION.RIGHT_OF_SIDEBAR}px;
    z-index: 9999;
    display: flex;
    flex-direction: column-reverse; /* Stack bottom to top */
    gap: 16px;
    pointer-events: none; /* Allow clicks through container */
    max-height: calc(100vh - ${TOAST_POSITION.BOTTOM * 2}px);
    overflow: visible;
    
    /* Smooth transition when sidebar collapses/expands */
    transition: left 0.2s ease-in-out;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * ToastManager component
 * Renders all active toasts in a portal at the toast-root container level.
 */
const ToastManager = () => {
    const dispatch = useDispatch();
    const toasts = useSelector(toastsSelector);
    const isSidebarCollapsed = useSelector(sidebarCollapsedSelector);
    
    // Calculate sidebar width based on collapsed state
    const sidebarWidth = useMemo(() => {
        return isSidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
    }, [isSidebarCollapsed]);
    
    // Handle toast dismissal
    const handleDismiss = (id) => {
        dispatch(dismissToast(id));
    };
    
    // Get the toast portal container, fallback to body if not found
    const portalRoot = document.getElementById(TOAST_PORTAL_ROOT_ID) || document.body;
    
    // Don't render portal if no toasts
    if (toasts.length === 0) {
        return null;
    }
    
    // Render toasts in a portal
    return createPortal(
        <ToastContainer 
            $sidebarWidth={sidebarWidth}
            role="region"
            aria-label="Notifications"
            aria-live="polite"
        >
            {toasts.map((toast) => (
                <LytToast
                    key={toast.id}
                    id={toast.id}
                    type={toast.type}
                    title={toast.title}
                    content={toast.content}
                    timestamp={toast.timestamp}
                    actions={toast.actions}
                    autoClose={toast.autoClose}
                    duration={toast.duration}
                    onDismiss={handleDismiss}
                />
            ))}
        </ToastContainer>,
        portalRoot
    );
};

export default ToastManager;
